import { useEffect, useLayoutEffect, type ReactNode } from "react";
import { ConfigProvider, type ThemeConfig } from "./ui/antd";
import { lucideComponentDefaults } from "./ui/antd";
import "./admin-ice.css";
import "./admin-controls.css";

export const adminIceTheme: ThemeConfig = {
  token: {
    colorPrimary: "#397d70",
    colorPrimaryHover: "#2f6c61", colorPrimaryActive: "#24574e",
    colorInfo: "#297cb4",
    colorSuccess: "#07856c",
    colorWarning: "#ad751c",
    colorError: "#bf4554",
    colorPrimaryBg: "#e0f2eb", colorPrimaryBgHover: "#ccf2e7",
    colorPrimaryBorder: "#a6d4c5", colorPrimaryText: "#24685f",
    colorPrimaryTextHover: "#164f47", colorPrimaryTextActive: "#103d36",
    colorLink: "#24685f", colorLinkHover: "#164f47", colorLinkActive: "#103d36",
    colorInfoBg: "#edf5fc", colorInfoBorder: "#c6dfee", colorInfoText: "#286f9d",
    colorSuccessBg: "#eaf6f1", colorSuccessBorder: "#c3e3d6", colorSuccessText: "#26755e",
    colorWarningBg: "#fbf6e9", colorWarningBorder: "#eadcba", colorWarningText: "#8b661d",
    colorErrorBg: "#fcf0f2", colorErrorBorder: "#eccdd3", colorErrorText: "#a73f51",
    colorText: "#183247",
    colorTextSecondary: "#3d586d",
    colorTextTertiary: "#526b80",
    colorTextPlaceholder: "#5d7486",
    colorBorder: "#d5dbe3",
    colorBorderSecondary: "#e1e5ea",
    colorBgLayout: "#f3f4f6",
    colorBgContainer: "#ffffff",
    colorBgElevated: "#ffffff",
    colorFillAlter: "#f5f6f8",
    colorFillSecondary: "#f0f2f5",
    colorBgContainerDisabled: "#f0f2f5",
    colorTextDisabled: "#8994a2",
    borderRadius: 8,
    borderRadiusLG: 16,
    fontSize: 13,
    controlHeight: 36,
    boxShadowSecondary: "0 12px 40px rgba(44, 75, 106, .12)",
  },
  components: {
    Button: {
      colorText: "#183247", primaryColor: "#1b3936",
      colorPrimary: "#ccf2e7", colorPrimaryHover: "#b7e6d8", colorPrimaryActive: "#9fd8c7",
      primaryShadow: "none", defaultShadow: "none",
      defaultBg: "#ffffff", defaultColor: "#183247", defaultBorderColor: "#cbd3dc",
      defaultHoverBg: "#eef1f4", defaultHoverColor: "#183247", defaultHoverBorderColor: "#aab7c4",
      defaultActiveBg: "#dfe5eb", defaultActiveColor: "#183247", defaultActiveBorderColor: "#8f9fac",
      textHoverBg: "#eef1f4",
    },
    Card: { headerBg: "transparent", headerFontSize: 15 },
    Tag: { defaultColor: "#203f56" },
    Table: { headerBg: "#f5f6f8", headerColor: "#3d586d", rowHoverBg: "#f3f5f7", borderColor: "#e1e5ea", cellPaddingBlock: 14, cellPaddingInline: 12 },
    Tabs: { inkBarColor: "#397d70", itemColor: "#3d586d", itemHoverColor: "#164f47", itemSelectedColor: "#24685f", itemActiveColor: "#103d36" },
    Segmented: { itemColor: "#3d586d", itemHoverColor: "#183247", itemHoverBg: "#e6eaef", itemActiveBg: "#9fd8c7", itemSelectedBg: "#ccf2e7", itemSelectedColor: "#1b3936" },
    Radio: { buttonBg: "#ffffff", buttonColor: "#3d586d", buttonSolidCheckedBg: "#ccf2e7", buttonSolidCheckedColor: "#1b3936", buttonSolidCheckedHoverBg: "#b7e6d8", buttonSolidCheckedActiveBg: "#9fd8c7" },
    Select: { optionActiveBg: "#eef1f4", optionSelectedBg: "#ccf2e7", optionSelectedColor: "#1b3936" },
    Tree: { nodeHoverBg: "#eef1f4", nodeSelectedBg: "#ccf2e7", nodeSelectedColor: "#1b3936" },
    Modal: { contentBg: "#ffffff", headerBg: "transparent", footerBg: "transparent" },
    Drawer: { colorBgElevated: "#ffffff" },
    Menu: {
      itemBg: "transparent", subMenuItemBg: "transparent", itemColor: "#c0cad5",
      itemHoverColor: "#edf1f5", itemSelectedColor: "#243c50", itemSelectedBg: "#dee8f1", itemHoverBg: "#3c4857",
      itemActiveBg: "#4a596a",
      darkItemBg: "transparent", darkSubMenuItemBg: "transparent",
      darkItemColor: "#c0cad5", darkItemHoverColor: "#edf1f5", darkItemSelectedColor: "#243c50",
      darkItemSelectedBg: "#dee8f1", darkItemHoverBg: "#3c4857",
    },
  },
};

/** Keep admin tokens and body-mounted overlays out of customer/public portals. */
export default function AdminAppearance({ enabled, pageKey, children }: { enabled: boolean; pageKey: string; children: ReactNode }) {
  useLayoutEffect(() => {
    if (enabled) window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [enabled, pageKey]);
  useEffect(() => {
    if (!enabled) return;
    document.body.classList.add("admin-ice-overlays");
    ConfigProvider.config({ holderRender: node => <ConfigProvider {...lucideComponentDefaults} theme={adminIceTheme}>{node}</ConfigProvider> });
    return () => {
      document.body.classList.remove("admin-ice-overlays");
      ConfigProvider.config({ holderRender: undefined });
    };
  }, [enabled]);
  return enabled ? <ConfigProvider theme={adminIceTheme}>{children}</ConfigProvider> : children;
}
